import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDigest, money } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { SkippedDigest } from "@/components/SkippedDigest";
import { cn } from "@/lib/utils";
import { CheckCircle2, Scissors, Send, Inbox, MessageSquare, Hourglass, Loader2, CalendarRange, ArrowUpRight, Banknote, Download, ShieldCheck } from "lucide-react";

const Kpi = ({ icon: Icon, label, value, sub, tone, testId }) => (
  <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon className={cn("h-3.5 w-3.5", tone)} /> {label}</div>
    <div className="font-mono text-2xl font-bold mt-1">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

const SavingsCard = ({ s, user, onOpen }) => {
  const teamOther = s.team_value - s.mine_value - s.rule_value;
  const pct = (v) => (s.team_value ? Math.max(0, Math.round((v / s.team_value) * 100)) : 0);
  return (
    <div className="rounded-xl border border-teal-500/30 bg-teal-500/5 overflow-hidden" data-testid="savings-card">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-teal-500/20 text-sm font-medium text-teal-700 dark:text-teal-400">
        <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Not paid twice</span>
        <span className="text-xs font-normal text-muted-foreground" data-testid="savings-all-time">All time: you've stopped {s.mine_all_time_count} duplicate{s.mine_all_time_count === 1 ? "" : "s"} · {money(s.mine_all_time_value)}</span>
      </div>
      <div className="grid gap-4 px-4 py-4 md:grid-cols-3">
        <div>
          <div className="text-xs text-muted-foreground">Your decisions this week</div>
          <div className="font-mono text-2xl font-bold mt-0.5" data-testid="savings-mine">{money(s.mine_value)}</div>
          <div className="text-xs text-muted-foreground">{s.mine_count ? `${s.mine_count} duplicate${s.mine_count === 1 ? "" : "s"} you rejected` : "No duplicates rejected by you this week"}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Whole team incl. the rule</div>
          <div className="font-mono text-2xl font-bold mt-0.5" data-testid="savings-team">{money(s.team_value)}</div>
          <div className="text-xs text-muted-foreground">{s.team_count} stopped · {s.rule_count} by the exact-match rule ({money(s.rule_value)})</div>
        </div>
        <div className="flex flex-col justify-center">
          <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted" data-testid="savings-bar">
            {s.mine_value > 0 && <div className="bg-teal-500" style={{ width: `${pct(s.mine_value)}%` }} title={`You · ${money(s.mine_value)}`} />}
            {teamOther > 0 && <div className="bg-sky-400" style={{ width: `${pct(teamOther)}%` }} title={`Teammates · ${money(teamOther)}`} />}
            {s.rule_value > 0 && <div className="bg-amber-400" style={{ width: `${pct(s.rule_value)}%` }} title={`Duplicate Rule · ${money(s.rule_value)}`} />}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-x-3 text-[11px] text-muted-foreground">
            <span><span className="inline-block h-2 w-2 rounded-full bg-teal-500 mr-1" />You {pct(s.mine_value)}%</span>
            <span><span className="inline-block h-2 w-2 rounded-full bg-sky-400 mr-1" />Teammates {pct(Math.max(0, teamOther))}%</span>
            <span><span className="inline-block h-2 w-2 rounded-full bg-amber-400 mr-1" />Rule {pct(s.rule_value)}%</span>
          </div>
        </div>
      </div>
      {s.items.length > 0 && (
        <ul className="divide-y divide-border border-t border-border" data-testid="savings-list">
          {s.items.map((i) => (
            <li key={i.id}>
              <button data-testid={`savings-${i.id}`} onClick={() => onOpen(i.id)} className="w-full flex items-center justify-between gap-3 px-4 py-2 text-left hover:bg-muted/40">
                <div className="min-w-0 text-xs">
                  <span className="font-medium">{i.supplier}</span> <span className="font-mono text-muted-foreground">{i.invoice_number}</span>
                  <span className="text-muted-foreground"> · duplicate of {i.match_number || "an existing invoice"} · {i.auto ? "Duplicate Rule" : i.by === user ? "you" : i.by} · {i.at ? new Date(i.at).toLocaleDateString() : ""}</span>
                </div>
                <span className="flex items-center gap-2 font-mono text-sm whitespace-nowrap">{money(i.total, i.currency)} <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" /></span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const EventList = ({ title, icon: Icon, items, empty, testId, onOpen }) => (
  <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid={testId}>
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border text-sm font-medium">
      <Icon className="h-4 w-4 text-primary" /> {title} <span className="font-mono text-xs text-muted-foreground">{items.length}</span>
    </div>
    {items.length === 0 ? (
      <div className="px-4 py-5 text-xs text-muted-foreground italic">{empty}</div>
    ) : (
      <ul className="divide-y divide-border max-h-56 overflow-auto d20-scroll">
        {items.map((e) => (
          <li key={e.id}>
            <button onClick={() => e.invoice_id && onOpen(e.invoice_id)} className="w-full text-left px-4 py-2 text-xs hover:bg-muted/40">
              <div className="truncate">{e.detail}</div>
              <div className="text-muted-foreground font-mono mt-0.5">{e.supplier} {e.invoice_number} · {new Date(e.timestamp).toLocaleString()}</div>
            </button>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default function MyWeek() {
  const { user } = useApp();
  const [week, setWeek] = useState("this");
  const [d, setD] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => getDigest(user, week).then(setD), [user, week]);
  useEffect(() => {
    setD(null);
    load();
  }, [load]);

  const max = d ? Math.max(1, ...d.activity.map((a) => a.count)) : 1;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">My Week</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            What <span className="text-foreground font-medium">{user}</span> did, and what's still waiting.
            {d && <span className="font-mono text-xs ml-2">{d.start} → {d.end}</span>}
          </p>
        </div>
        <div className="flex rounded-lg border border-border p-0.5 text-xs">
          {[["this", "This week"], ["last", "Last week"]].map(([k, l]) => (
            <button key={k} data-testid={`week-${k}`} onClick={() => setWeek(k)} className={cn("rounded-md px-3 py-1.5 font-medium transition-colors", week === k ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground")}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {!d ? (
        <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Kpi testId="kpi-approvals" icon={CheckCircle2} tone="text-emerald-500" label="Approved" value={d.totals.approvals} sub={money(d.totals.approved_value)} />
            <Kpi testId="kpi-splits" icon={Scissors} tone="text-purple-400" label="Splits / recodes" value={d.totals.splits + d.totals.recodes} sub={`${d.totals.splits} splits · ${d.totals.recodes} recodes`} />
            <Kpi testId="kpi-handoffs" icon={Send} tone="text-violet-400" label="Hand-offs" value={`${d.totals.handoffs_sent} / ${d.totals.handoffs_received}`} sub="sent / received" />
            <Kpi testId="kpi-comments" icon={MessageSquare} tone="text-pink-400" label="Notes" value={d.totals.comments} />
            <Kpi testId="kpi-payments" icon={Banknote} tone="text-sky-400" label="Payments · exports" value={`${d.totals.payments} · ${d.totals.exports}`} />
            <Kpi testId="kpi-waiting" icon={Hourglass} tone="text-amber-500" label="Waiting on me" value={d.totals.waiting_on_me} sub={money(d.totals.waiting_value)} />
          </div>

          <div className="rounded-xl border border-border bg-card p-4" data-testid="activity-strip">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3"><CalendarRange className="h-3.5 w-3.5" /> Activity by day · {d.totals.events} events</div>
            <div className="grid grid-cols-7 gap-2">
              {d.activity.map((a) => (
                <div key={a.day} className="text-center">
                  <div className="h-16 flex items-end justify-center">
                    <div className="w-full max-w-[40px] rounded-t-md bg-primary/70 transition-all" style={{ height: `${Math.max(4, (a.count / max) * 100)}%` }} title={`${a.count} events`} />
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1">{new Date(a.day + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" })}</div>
                  <div className="font-mono text-xs">{a.count}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 overflow-hidden" data-testid="waiting-list">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-amber-500/20 text-sm font-medium text-amber-500">
              <Hourglass className="h-4 w-4" /> Still waiting on you <span className="font-mono text-xs">{d.waiting.length}</span>
            </div>
            {d.waiting.length === 0 ? (
              <div className="px-4 py-5 text-xs text-muted-foreground">Inbox zero — nothing assigned to you is open.</div>
            ) : (
              <ul className="divide-y divide-border">
                {d.waiting.map((w) => (
                  <li key={w.id}>
                    <button data-testid={`waiting-${w.id}`} onClick={() => navigate(`/invoice/${w.id}`)} className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-muted/40">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{w.supplier} <span className="font-mono text-xs text-muted-foreground">{w.invoice_number}</span></div>
                        <div className="text-xs text-muted-foreground">from {w.assigned_by} · {w.assigned_at ? new Date(w.assigned_at).toLocaleDateString() : ""}{w.due_date ? ` · due ${w.due_date}` : ""}</div>
                      </div>
                      <span className="flex items-center gap-2 font-mono text-sm">{money(w.total, w.currency)} <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" /></span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {d.savings && <SavingsCard s={d.savings} user={d.user} onOpen={(id) => navigate(`/invoice/${id}`)} />}

          <SkippedDigest items={d.skipped || []} total={d.skipped_total || 0} onChanged={load} />

          <div className="grid gap-4 md:grid-cols-2">
            <EventList testId="list-approvals" title="Approvals" icon={CheckCircle2} items={d.approvals} empty="No approvals this week." onOpen={(id) => navigate(`/invoice/${id}`)} />
            <EventList testId="list-splits" title="Splits & recodes" icon={Scissors} items={[...d.splits, ...d.recodes]} empty="No coding changes this week." onOpen={(id) => navigate(`/invoice/${id}`)} />
            <EventList testId="list-handoffs-sent" title="Hand-offs you sent" icon={Send} items={d.handoffs_sent} empty="You didn't hand anything off." onOpen={(id) => navigate(`/invoice/${id}`)} />
            <EventList testId="list-handoffs-received" title="Hand-offs you received" icon={Inbox} items={d.handoffs_received} empty="Nothing was handed to you." onOpen={(id) => navigate(`/invoice/${id}`)} />
            <EventList testId="list-comments" title="Notes you left" icon={MessageSquare} items={d.comments} empty="No notes this week." onOpen={(id) => navigate(`/invoice/${id}`)} />
            <div className="rounded-xl border border-dashed border-border p-4 text-xs text-muted-foreground flex items-center gap-2">
              <Download className="h-4 w-4" /> Tip: switch reviewer in the header to see a teammate's week.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
